import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { EntityId, ApprovalRequestStatus, IApprovalRequestCategory, CATEGORY, IApprovalRequest, ROUTES, LimitTime, UnitTime } from '@appshared';

import { ApprovalRequestApiService } from '../services/approval-request-api.service';
import { RejectRequestDialogComponent } from '../reject-request-dialog';
import { FormGroup } from '@angular/forms';
import { NewApprovalRequestService } from './new-approval-request.service';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { IApprovalRequestType } from 'app/shared/model/approval-request-type.model';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ApprovalRequestLogicService {
  private _confirmDialogRef: MatDialogRef<any>;

  constructor(
    public http: HttpClient,
    private _matDialog: MatDialog,
    private _translate: TranslateService,
    private _fuseProgressBarService: FuseProgressBarService,
    private _approvalRequestApiSrv: ApprovalRequestApiService,
    private _newApprovalRequestSrv: NewApprovalRequestService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  approve(reqIds: EntityId[], name?: string) {
    return new Promise<boolean>((resolve, reject) => {
      const totalOfSelectedRequests = reqIds.length;
      this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
        disableClose: true,
      });
      this._confirmDialogRef.componentInstance.data = {
        confirmHeader: this._translate.instant('DIALOG.APPROVE_SINGLE_REQUEST.HEADER'),
        confirmMessage: this._translate.instant('DIALOG.APPROVE_SINGLE_REQUEST.MESSAGE', { name: name }),
        acceptBtnText: this._translate.instant('DIALOG.APPROVE_SINGLE_REQUEST.OK_BTN_TEXT'),
        cancelBtnText: this._translate.instant('DIALOG.APPROVE_SINGLE_REQUEST.CANCEL_BTN_TEXT'),
      };
      if (totalOfSelectedRequests > 1) {
        this._confirmDialogRef.componentInstance.data.confirmMessage = this._translate.instant('DIALOG.APPROVE_REQUESTS.MESSAGE', {
          count: totalOfSelectedRequests,
        });
      }

      this._confirmDialogRef.afterClosed().subscribe(result => {
        // confirmed to approve
        if (result && reqIds) {
          this.updateStatus(reqIds, ApprovalRequestStatus.APPROVED).then(
            res => {
              this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
                disableClose: true,
              });
              this._confirmDialogRef.componentInstance.data = {
                svgIcon: 'artstuff_success_response',
                confirmHeader: this._translate.instant('DIALOG.RESPONSE.APPROVED_HEADER'),
                confirmMessage: this._translate.instant('DIALOG.RESPONSE.APPROVED_MESSAGE'),
                cancelBtnText: this._translate.instant('DIALOG.RESPONSE.CLOSE_BUTTON_TEXT'),
                isResponse: true,
                hideAcceptBtn: true,
              };

              this._confirmDialogRef.afterClosed().subscribe(result => {
                resolve(true);
              });
            },
            err => {
              reject(err);
            }
          );
        }
        // not confirmed to approve
        else {
          resolve(false);
        }
      });
    });
  }

  reject(reqIds: EntityId[]) {
    return new Promise<boolean>((resolve, reject) => {
      this._confirmDialogRef = this._matDialog.open(RejectRequestDialogComponent, {
        disableClose: true,
      });
      this._confirmDialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.updateStatus(reqIds, ApprovalRequestStatus.REJECTED, result).then(
            res => {
              resolve(true);
            },
            err => {
              reject();
            }
          );
        } else {
          reject();
        }
      });
    });
  }

  private updateStatus(ids: EntityId[], status: ApprovalRequestStatus, reason?: string) {
    return new Promise<boolean>((resolve, reject) => {
      this._fuseProgressBarService.show();
      this._confirmDialogRef = null;
      this._approvalRequestApiSrv.updateStatus(ids, status, reason).subscribe(
        res => {
          this._fuseProgressBarService.hide();
          resolve(true);
        },
        err => {
          this._fuseProgressBarService.hide();
          reject(this.translateErrorMessage(err, null, null, null));
        }
      );
    });
  }

  onSubmitData(formGroup: FormGroup, requestType: IApprovalRequestType, category: IApprovalRequestCategory, router: Router, totalLeaveDays: number, limitTime: LimitTime) {
    return new Promise<Boolean>((resolve, reject) => {
      let formData = this._newApprovalRequestSrv.transformFormData(formGroup);
      formData['requestType'] = requestType?.code;
      formData['categoryId'] = category?.id;
      formData['totalLeaveDays'] = totalLeaveDays;
      this._approvalRequestApiSrv.save(formData).subscribe(
        (res: IApprovalRequest) => {
          this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
            disableClose: true,
          });
          this._confirmDialogRef.componentInstance.data = {
            svgIcon: 'artstuff_success_response',
            confirmHeader: this._translate.instant('DIALOG.RESPONSE.SENT_HEADER'),
            confirmMessage: this._translate.instant('DIALOG.RESPONSE.SENT_MESSAGE', {
              id: res.code,
            }),
            acceptBtnText: this._translate.instant('DIALOG.RESPONSE.VIEW_BUTTON_TEXT'),
            cancelBtnText: this._translate.instant('DIALOG.RESPONSE.CLOSE_BUTTON_TEXT'),
            isResponse: true,
          };
          this._confirmDialogRef.afterClosed().subscribe(result => {
            // router.navigate(['/hrm/approval-requests/my-requests']);

            if (result) {
              //View: redirected to the corresponding request detail page
              router.navigate(['/hrm/approval-requests/my-requests', res.id, 'view']);
            } else {
              //Close: back to previous page
              router.navigate([ROUTES.REQUEST_NEW]);
            }
          });
          resolve(true);
        },
        err => {
          reject(this.translateErrorMessage(err, formGroup, category, limitTime));
        }
      );
    });
  }

  /* Handle translating error messages from the BE server */
  private translateErrorMessage(errorResponse: any, formGroup: FormGroup, category: IApprovalRequestCategory, limitTime: LimitTime): object {
    const regexErrorMessPattern = '^ERROR_CODE.?';

    switch (errorResponse.error.errorCode) {
      case 'POSER-REQ-002':
        errorResponse.error.errorMessage = this._translate.instant(
          'ERROR_CODE.' + (errorResponse.error.errorCode ? errorResponse.error.errorCode : 'UNDEFINED'),
          {
            limit_time_value: limitTime.value,
            limit_time_unit:
              limitTime.unit == UnitTime.DAY
                ? limitTime.value > 1
                  ? this._translate.instant('APPROVAL_REQUESTS.DAYS')
                  : this._translate.instant('APPROVAL_REQUESTS.DAY')
                : limitTime.value > 1
                ? this._translate.instant('APPROVAL_REQUESTS.HOURS')
                : this._translate.instant('APPROVAL_REQUESTS.HOUR'),
          }
        );
        break;
      case 'POSER-REQ-007':
        errorResponse.error.errorMessage =
          category?.type == CATEGORY.TIMESHEET
            ? this._translate.instant('NEW_REQUEST.ERROR.INVALID_DATE')
            : this._translate.instant('ERROR_CODE.' + (errorResponse.error.errorCode ? errorResponse.error.errorCode : 'UNDEFINED'));
        break;
      case 'POSER-REQ-025':
        errorResponse.error.errorMessage = this._translate.instant('ERROR_CODE.' + (errorResponse.error.errorCode ? errorResponse.error.errorCode : 'UNDEFINED'),
          {
            availableOTHours: errorResponse.error.errorParams["availableOTHours"]
          });
        break;
      default:
        errorResponse.error.errorMessage = this._translate.instant('ERROR_CODE.' + (errorResponse.error.errorCode ? errorResponse.error.errorCode : 'UNDEFINED'));
        break;
    }

    //Check invalid time for PART_TIME and LEAVE_TIME form
    const regexInvalidCodePattern = 'SER-CO-[0-9]{3}';
    if (
      (errorResponse.error.errorMessage.match(regexErrorMessPattern) || errorResponse.error.errorCode.match(regexInvalidCodePattern)) &&
      (category?.type == CATEGORY.PART_TIME || category?.type == CATEGORY.LEAVE_TIME)
    ) {
      let startTime = moment(formGroup.get('startTime').value, 'HH:mm');
      let endTime = moment(formGroup.get('endTime').value, 'HH:mm');
      errorResponse.error.errorMessage =
        endTime.isBefore(startTime) || endTime.isSame(startTime)
          ? this._translate.instant('NEW_REQUEST.ERROR.INVALID_TIME')
          : errorResponse.error.errorCode;
    }

    return errorResponse;
  }

  public getEventDate(request: IApprovalRequest): string {
    switch (request.category.type){
      case CATEGORY.LEAVE_TIME:
        if (this.isSameDays(request)) {
          return this.getFormatDate(request.startDate);
        }else {
          return `${this.getFormatDate(request.startDate)} - ${this.getFormatDate(request.endDate)}`;
        }
      case CATEGORY.OVERTIME:
      case CATEGORY.PART_TIME:
        return this.getFormatDate(request.startDate);
      default:
          return this.getFormatDate(request.eventDate);
    }
  }

  private isSameDays(request: IApprovalRequest): boolean {
    const startDay = moment(new Date(request.startDate * 1000));
    const endDay = moment(new Date(request.endDate * 1000));
    return  startDay.startOf('day').isSame(endDay.startOf('day'));
  }

  private getFormatDate(secondsTime: number): string {
    return formatDate(secondsTime * 1000, 'dd/MM/yyyy', this.locale);
  }
}
